import React, { Dispatch, useState } from 'react';
import { t } from 'Locale/index';
import { Pressable, View } from 'react-native';
import { LanguageSettingsContext, StyleMap } from 'types';
import { IAudioTrack, ITextTrack } from '@24i/player-base';
import { PlayerManager } from '@24i/react-native-player';
import { Device } from 'DeviceUtils';
import { useLanguageSettingsContext } from 'LocalGluons';
import { logEvent } from 'AppManager';
import { getTrackLabel } from '../GetTrackLabel';
import defaultStyles from './LanguageModalStyle.jss';
import Icon from '../../../Icon';
import AudioTextTrackSelectBox from '../AudioTextTrackSelectBox/AudioTextTrackSelectBox';

const OFF_ITEM_ID = 'text_off';
const EMPTY_ITEM_ID = 'empty';
const EMPTY_VALUE = '--';

interface LanguageModalProps {
    style: StyleMap;
    onClose: () => void;
    audioTracks: IAudioTrack[];
    textTracks: ITextTrack[];
    playerInstance: PlayerManager;
    setSelectedAudioTrack: Dispatch<IAudioTrack>;
    selectedAudioTrack: IAudioTrack;
    setSelectedTextTrack: Dispatch<ITextTrack>;
    selectedTextTrack: ITextTrack;
}

const LanguageModal = ({
    style,
    onClose,
    audioTracks: playerAudioTracks,
    textTracks: playerTextTracks,
    selectedAudioTrack,
    playerInstance,
    setSelectedAudioTrack,
    setSelectedTextTrack,
    selectedTextTrack,
}: LanguageModalProps) => {
    const {
        setSelectedPlayerAudioId,
        getAudioTrackLabel
    } = useLanguageSettingsContext() as LanguageSettingsContext;

    const parseAudioTracks = (unparsedTracks: IAudioTrack[]) => {
        const tracks = unparsedTracks || [];
        return tracks.map((audioTrack) => ({
            ...audioTrack,
            isSelected: audioTrack.selected,
            language: getTrackLabel(audioTrack)
        }));
    };

    const parsedAudioTracks = parseAudioTracks(playerAudioTracks);
    const [audioTracks, setAudioTracksParsed] = useState([...parsedAudioTracks]);

    const onSelectAudioTrack = (item: IAudioTrack) => {
        const newAudioTracks = audioTracks.map(audioTrack => ({
            ...audioTrack,
            selected: audioTrack.id === item.id ?
                !!setSelectedPlayerAudioId(audioTrack.id) : false,
        }));
        logEvent({
            type: 'user-flow',
            subtype: 'player/ma-sub',
            content1: "audio",
            content2: item.language,
            context: "/player"
        });
        if (!Device.isWeb) {
            playerInstance.audioTrack = item;
        }
        setSelectedAudioTrack(item);
        setAudioTracksParsed(newAudioTracks);
        onClose();
    };
    const selectedAudioTracks = audioTracks.map(audioTrack =>
    ({
        ...audioTrack,
        isSelected: audioTrack.id === selectedAudioTrack.id,
        language: getAudioTrackLabel(audioTrack)
    })
    );


    //Start of subtitles section
    const { setSelectedPlayerTextId, getTextTrackLabel } =
        useLanguageSettingsContext() as LanguageSettingsContext;

    const parseTextTracks = (textTracks: ITextTrack[]) => {
        const language = (textTrack: ITextTrack) => {
            if (textTrack.id === OFF_ITEM_ID || textTrack.id === EMPTY_ITEM_ID) {
                return EMPTY_VALUE;
            }
            return getTrackLabel(textTrack);
        };

        const tracks = textTracks || [];
        return tracks.map((textTrack) => ({
            ...textTrack,
            language: language(textTrack),
            isSelected: textTrack.selected

        }));
    };

    const parsedTextTracks = parseTextTracks(playerTextTracks);
    const [textTracks, setTextTracks] = useState([...parsedTextTracks]);

    const onSelectTextTrack = (item: ITextTrack) => {
        const newTextTracks = textTracks.map((textTrack) => ({
            ...textTrack,
            selected: textTrack.id === item.id ?
                !!setSelectedPlayerTextId(textTrack.id) : false
        }));

        logEvent({
            type: 'user-flow',
            subtype: 'player/ma-sub',
            content1: "subtitles",
            content2: item.language,
            context: "/player"
        });
        playerInstance.textTrack = item;
        setSelectedTextTrack(item);
        setTextTracks(newTextTracks);
        onClose();
    };

    const selectedTextTracks = textTracks.map((textTrack) =>
    ({
        ...textTrack, isSelected: textTrack.id === selectedTextTrack.id,
        language: getTextTrackLabel(textTrack)
    }));


    return (
        <View style={[defaultStyles.container, style]}>
            <View style={defaultStyles.backgroundContainer} />
            <View style={defaultStyles.containerHeight}>
                <View style={defaultStyles.innerContainer}>
                    {audioTracks.length > 1 && (
                        <AudioTextTrackSelectBox
                            title={t('COMMON.AUDIO')}
                            defaultStyles={defaultStyles}
                            onSelect={
                                onSelectAudioTrack as
                                (item: IAudioTrack | ITextTrack) => void
                            }
                            selectedMedia={selectedAudioTracks}
                        />
                    )}
                    {textTracks.length > 1 && (
                        <AudioTextTrackSelectBox
                            title={t('COMMON.SUBTITLES')}
                            defaultStyles={defaultStyles}
                            onSelect={onSelectTextTrack}
                            selectedMedia={selectedTextTracks}
                        />
                    )}
                </View>
            </View>
            <View style={defaultStyles.iconClose}>
                <Pressable onPress={onClose}>
                    <Icon style={{ zIndex: 999999 }} name="close" />
                </Pressable>
            </View>

        </View>
    );
};
export default LanguageModal;
